import { useGLTF, useTexture } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { MeshBasicMaterial, MeshStandardMaterial, RepeatWrapping, SRGBColorSpace, Box3, Vector3, Mesh } from "three";
import InfoPanel from "./InfoPanel";
import { extractGlbFromZip, loadDirectGlbFile } from "../utils/glbLoader";
import WireframeShader from "./WireframeShader";

const Model = (props) => {

    const ref = useRef();
    const [glbUrl, setGlbUrl] = useState(null);
    const [initialMaterialData, setInitialMaterialData] = useState({});
    const [wireframeMeshes, setWireframeMeshes] = useState([]);

    const uvCheckerMap = useTexture('./textures/UVChecker.png');
    const loadedPatterns = {};

    const utilMats = {
        uvCheckMaterial: new MeshStandardMaterial(),
        patternMaterial: new MeshStandardMaterial()
    }

    const initUtilMats = () => {
        // Only initialize if texture is loaded
        if (uvCheckerMap) {
            utilMats.uvCheckMaterial.name = "UV-Check-Material";
            utilMats.uvCheckMaterial.map = uvCheckerMap;
            utilMats.uvCheckMaterial.map.colorSpace = SRGBColorSpace;
            utilMats.uvCheckMaterial.map.wrapS = RepeatWrapping;
            utilMats.uvCheckMaterial.map.wrapT = RepeatWrapping;
            utilMats.uvCheckMaterial.map.flipY = false;
            utilMats.uvCheckMaterial.map.repeat.set(props.uvScale, props.uvScale);
            utilMats.uvCheckMaterial.roughness = 1.0;
        }


        utilMats.patternMaterial.name = "Pattern-Material";
        props.patternOptions.map((pattern) => {
            initPatternMap(pattern);
        })
    }

    const initPatternMap = (pattern) => {
        let map = useTexture(pattern.image);
        map.colorSpace = SRGBColorSpace;
        map.wrapS = RepeatWrapping;
        map.wrapT = RepeatWrapping;
        map.flipY = false;
        map.repeat.set(props.xRepeat, props.yRepeat);
        map.center.set(0, 1);
        map.roughness = 1.0;
        loadedPatterns[pattern.value] = map;
    }

    initUtilMats()

    useEffect(() => {
        const loadModel = async () => {
            try {
                const isZipFile = props.url.toLowerCase().endsWith('.zip');

                if (isZipFile) {
                    const { glbUrl: extractedGlbUrl } = await extractGlbFromZip(props.url);
                    setGlbUrl(extractedGlbUrl);
                } else {
                    const { glbUrl: directGlbUrl } = await loadDirectGlbFile(props.url);
                    setGlbUrl(directGlbUrl);
                }
                
            } catch (err) {
                console.error('Error loading GLB model:', err);
                const errorMessage = err.message || 'Failed to load GLB model';
                
                if (props.onModelError) {
                    props.onModelError(errorMessage);
                } else if (props.onLoadingStatus) {
                    props.onLoadingStatus('error', errorMessage);
                }
            }
        };

        if (props.url) {
            loadModel();
        }
    }, [props.url]);

    const gltf = glbUrl ? useGLTF(glbUrl) : null;

    const extractMaterialData = () => {
        if (!ref.current) return;
        
        let uniqueMaterialNodes = new Set();
        ref.current.traverse((child) => {
            if (child.isMesh) {
                uniqueMaterialNodes.add(child.material?.name);
                initialMaterialData[child.name] = child.material;
                setInitialMaterialData(initialMaterialData);
            }
        });
        props.onSetMaterialNodes(uniqueMaterialNodes);
    }

    const cleanupWireframeMeshes = () => {
        wireframeMeshes.forEach(wireframeMesh => {
            if (wireframeMesh.parent) {
                wireframeMesh.parent.remove(wireframeMesh);
            }
        });
        setWireframeMeshes([]);
    }

    const UpdateMaterial = () => {
        if (!ref.current) return;
        
        // Clean up existing wireframe meshes first
        cleanupWireframeMeshes();
        
        ref.current.traverse((child) => {
            if (child.isMesh) {
                if (props.wireframe) {
                    // Set original material with transparency for wireframe overlay
                    const baseMat = initialMaterialData[child.name];
                    child.material = baseMat ? baseMat.clone() : child.material;
                    if (child.material) {
                        child.material.transparent = true;
                        child.material.opacity = props.wireframeOpacity || 0.5;
                    }
                }
                else if (props.uvChecker) {
                    if (utilMats.uvCheckMaterial && utilMats.uvCheckMaterial.map) {
                        child.material = utilMats.uvCheckMaterial;
                    } else {
                        child.material = initialMaterialData[child.name] || child.material;
                    }
                }
                else if (props.patternsMode && props.selectedPattern && initialMaterialData[child.name]?.name == props.selectedMaterialNode) {
                    utilMats.patternMaterial.map = loadedPatterns[props.selectedPattern];
                    utilMats.patternMaterial.map.rotation = props.selectedPatternRotation;
                    child.material = utilMats.patternMaterial;
                }
                else {
                    // Reset material properties when not in wireframe mode
                    child.material = initialMaterialData[child.name];
                }
            }
        });
    }

    const constructTreeData = (node) => {
        let obj = {};

        obj.title = node.name;
        obj.key = node.name;

        if (node.isMesh) {
            obj.material = node.material.name;
        }

        if (node.children.length > 0) {
            obj.children = [];
            for (let i = 0; i < node.children.length; i++) {
                let childObj = constructTreeData(node.children[i]);
                obj.children.push(childObj);
                if (childObj.key && props.metadata && props.metadata[childObj.key]) {
                    const childNode = {title:<InfoPanel
                        nodeClass={props.metadata[childObj.key].class}
                        vertexCount={props.metadata[childObj.key].vertex_count}
                        polygonCount={props.metadata[childObj.key].polygon_count}
                        quadsCount={props.metadata[childObj.key].quads}
                        triCount={props.metadata[childObj.key].tri}
                        ngonsCount={props.metadata[childObj.key].ngons}
                        materialName={props.metadata[childObj.key].material_name}
                        materialClass={props.metadata[childObj.key].material_class}
                        modifiers={props.metadata[childObj.key].modifiers}
                        texelDensity={props.metadata[childObj.key].texel_density}
                    />, checkable:false}
                    
                    if (childObj.children) {
                        childObj.children.unshift(childNode);
                    }
                    else {
                        childObj.children = [childNode]
                    }
                }
            }
        }

        return obj;
    }

    useEffect(() => {
        if (gltf && ref.current) {
            let treeData = [constructTreeData(ref.current.children[0])];
            extractMaterialData();
            props.onModelLoaded(treeData);
        }
    }, [gltf]);

    useEffect(() => {
        if (props.visibleNodes != undefined && ref.current) {
            ref.current.traverse((child) => {
                if (child.isMesh) {
                    if (props.visibleNodes.includes(child.name)) {
                        child.visible = true;
                    }
                    else {
                        child.visible = false;
                    }
                }
            });
        }
    }, [props.visibleNodes]);

    useEffect(() => {
        if (gltf) {
            UpdateMaterial();
            const box = new Box3().setFromObject(gltf.scene);
            const size = box.getSize(new Vector3());
            props.setModelSize(size);
        }
    }, [gltf, props.uvChecker, props.wireframe, props.wireframeOpacity, props.wireframeThickness, props.uvScale, 
        props.patternsMode, props.selectedPattern, props.selectedPatternRotation, 
        props.selectedMaterialNode, props.xRepeat, props.yRepeat]);

    // Cleanup wireframe meshes on unmount
    useEffect(() => {
        return () => {
            cleanupWireframeMeshes();
        };
    }, []);


    if (!gltf) {
        return null;
    }

    const renderWireframes = () => {
        if (!props.wireframe || !ref.current) return null;
        
        const wireframeComponents = [];
        ref.current.traverse((child) => {
            if (child.isMesh) {
                wireframeComponents.push(
                    <WireframeShader
                        key={`wireframe-${child.name}`}
                        geometry={child.geometry}
                        position={child.position}
                        rotation={child.rotation}
                        scale={[...child.scale.toArray()].map(s => s * 1.001)} // Slightly scale to prevent z-fighting
                        wireframeThickness={props.wireframeThickness || 1.0}
                        wireframeColor={[0.05, 0.05, 0.05]}
                    />
                );
            }
        });
        return wireframeComponents;
    };

    return (
        <>
            <primitive ref={ref} object={gltf.scene} />
            {renderWireframes()}
        </>
    );
};

export default Model;